// client/src/components/LandingPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useAnimation, useInView, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  FaFutbol, FaTrophy, FaUsers, FaGem, FaGamepad, FaFire, 
  FaArrowRight, FaChartLine, FaShieldAlt, FaExchangeAlt, FaGlobe,
  FaBars, FaTimes, FaStar, FaMedal, FaLock, FaCheckCircle,
  FaMagic, FaBullseye, FaHandsHelping, FaRunning, FaRegHandshake, FaMagnet, FaCrown,
  FaEnvelope, FaUser, FaPaperPlane, FaInfoCircle, FaBuilding, FaHashtag,
  FaCoffee, FaBolt, FaCompress, FaTachometerAlt, FaHeartbeat, FaUserSecret
} from 'react-icons/fa';
import { GiSoccerField, GiCardExchange, GiTrophyCup, GiSoccerBall, GiShield, GiSpeedometer } from 'react-icons/gi';
import { login, register, verifyEmail, forgotPassword, resetPassword, fetchLandingStats, resendVerification } from '../services/api';
import API from '../services/api';
import AnimeAvatar from './AnimeAvatar';
import LanguageSelector from './LanguageSelector';
import { QRCodeCanvas } from 'qrcode.react';


// ========== دالة التحقق من قوة كلمة المرور ==========
const isStrongPassword = (password) => {
  const strongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
  return strongRegex.test(password);
};

// ========== Animated Counter Component ==========
const Counter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const isInView = useInView(countRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);

  return <span ref={countRef}>{count.toLocaleString()}{suffix}</span>;
};

// ========== Animated Feature Card ==========
const FeatureCard = ({ icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    whileHover={{ y: -10, scale: 1.02 }}
    className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-yellow-500/50 transition-all duration-300"
  >
    <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/0 to-yellow-500/0 group-hover:from-yellow-500/5 group-hover:to-yellow-500/10 rounded-2xl transition-all duration-300" />
    <div className="text-yellow-400 text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">{icon}</div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

// ========== Enhanced Flip Card with All 6 Stats ==========
const FlipCard = ({ player }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  
  const getBorderStyle = () => {
    switch(player.rarity) {
      case 'gold': return 'border-yellow-400/80 shadow-yellow-500/30';
      case 'silver': return 'border-gray-300/80 shadow-gray-400/30';
      case 'bronze': return 'border-amber-600/80 shadow-amber-700/30';
      default: return 'border-white/30';
    }
  };

  const getAbilityIcon = (ability) => {
    const icons = {
      'Super Scorer': <FaBullseye />,
      'Super Saver': <FaHandsHelping />,
      'Playmaker': <FaMagic />,
      'Power Builder': <FaShieldAlt />,
      'Speed Runner': <FaRunning />,
      'Clean Man': <FaRegHandshake />,
      'Penalty Magnet': <FaMagnet />
    };
    return icons[ability] || <FaStar />;
  };

  return (
    <div className="relative w-64 h-80 cursor-pointer perspective" onClick={() => setIsFlipped(!isFlipped)}>
      <motion.div
        className="relative w-full h-full transition-all duration-500 transform-style-3d"
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 200 }}
      >
        {/* Front Side */}
        <div className={`absolute w-full h-full backface-hidden rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border-2 ${getBorderStyle()} shadow-2xl overflow-hidden`}>
          <div className="absolute top-2 left-2">
            {player.rarity === 'gold' && <span className="text-yellow-400 text-xl">👑</span>}
            {player.rarity === 'silver' && <span className="text-gray-300 text-xl">⭐</span>}
            {player.rarity === 'bronze' && <span className="text-amber-500 text-xl">🟤</span>}
            {player.rarity === 'common' && <span className="text-blue-400 text-xl">🛡️</span>}
          </div>
          <div className="absolute top-2 right-2 bg-black/60 rounded-full px-2 py-0.5 text-[10px] font-mono text-yellow-300">
            LVL {Math.floor((player.attack + player.defense + player.speed + player.accuracy + player.strength + player.tactics) / 6)}
          </div>
          <div className="absolute top-6 left-0 right-0 flex justify-center">
            <AnimeAvatar position={player.position} rarity={player.rarity} animated={false} className="w-28 h-28" />
          </div>
          <div className="absolute bottom-14 left-0 right-0 text-center">
            <h3 className="text-white font-bold text-lg mt-2">{player.name}</h3>
            <p className="text-yellow-400 text-sm mt-1">{player.position}</p>
            {player.special_ability && (
              <div className="flex items-center justify-center gap-1 mt-2 text-xs text-purple-300">
                {getAbilityIcon(player.special_ability)}
                <span>{player.special_ability}</span>
              </div>
            )}
          </div>
          <div className="absolute bottom-2 left-0 right-0 text-center text-[10px] text-gray-500">
            tap to flip
          </div>
        </div>

        {/* Back Side - All 6 Stats */}
        <div className="absolute w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-gray-900 to-black rounded-xl border-2 border-yellow-400/60 p-4 flex flex-col justify-center">
          <div className="text-center mb-3">
            <span className="text-white text-sm font-bold flex items-center justify-center gap-2">
              <FaChartLine /> FULL STATS
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            <div className="flex justify-between"><span className="text-gray-400">⚽ Attack:</span><span className="text-white font-bold">{player.attack}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">🛡️ Defense:</span><span className="text-white font-bold">{player.defense}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">⚡ Speed:</span><span className="text-white font-bold">{player.speed}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">🎯 Accuracy:</span><span className="text-white font-bold">{player.accuracy}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">💪 Strength:</span><span className="text-white font-bold">{player.strength || 50}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">🧠 Tactics:</span><span className="text-white font-bold">{player.tactics || 50}</span></div>
          </div>
          <div className="mt-3 pt-2 border-t border-white/20 text-center text-yellow-400 text-[10px] flex items-center justify-center gap-1">
            <FaExchangeAlt /> tap to flip back
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ============================================================
// ========== SponsorshipSection (مكون مستقل) ==========
// ============================================================
const SponsorshipSection = ({
  tiers,
  selectedTier,
  setSelectedTier,
  formData,
  onInputChange,
  onSubmit,
  loading,
  success,
  error,
  t,
  isModalOpen,
  openModal,
  closeModal,
}) => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-50px" });

  // Animation variants for cards
  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: i * 0.15,
        type: 'spring',
        stiffness: 200,
        damping: 20,
      },
    }),
    hover: {
      y: -15,
      scale: 1.02,
      transition: { type: 'spring', stiffness: 300, damping: 15 },
    },
  };

  const glowVariants = {
    animate: {
      scale: [1, 1.05, 1],
      opacity: [0.3, 0.6, 0.3],
      transition: { repeat: Infinity, duration: 3, ease: 'easeInOut' },
    },
  };

  const handleSelectTier = (tierId) => {
    setSelectedTier(tierId);
    openModal();
  };

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-gradient-to-b from-black/40 to-transparent">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 bg-clip-text text-transparent">
            {t('sponsorship.title')}
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            {t('sponsorship.subtitle')}
          </p>
        </motion.div>

        {/* Tiers Cards - مع توزيع متساوٍ وأزرار في الأسفل */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {tiers.map((tier, idx) => {
            const isSelected = selectedTier === tier.id;

            return (
              <motion.div
                key={tier.id}
                custom={idx}
                initial="hidden"
                animate={inView ? 'visible' : 'hidden'}
                variants={cardVariants}
                whileHover="hover"
                className={`relative rounded-2xl p-6 border-2 transition-all duration-300 flex flex-col min-h-[480px] ${
                  isSelected
                    ? `bg-gradient-to-br ${tier.color} border-white shadow-2xl scale-105`
                    : 'bg-gray-900/80 hover:bg-gray-800/80 border-white/10 hover:border-yellow-400/40'
                }`}
                style={{
                  background: isSelected ? undefined : 'rgba(17, 24, 39, 0.8)',
                }}
              >
                {!isSelected && (
                  <motion.div
                    variants={glowVariants}
                    animate="animate"
                    className={`absolute -inset-1 ${tier.glowColor} blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />
                )}

                <div className="relative z-10 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-4xl">{tier.icon}</div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-white">{tier.price}</div>
                      <div className="text-xs text-gray-400">{t(tier.periodKey)}</div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-2">{t(tier.titleKey)}</h3>
                  <p className="text-sm text-gray-300 mb-4">
                    {t('sponsorship.spots', { count: tier.spots })}
                  </p>

                  <ul className="space-y-2 mb-6 flex-1">
                    {tier.features.map((featureKey, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 0.1 * idx }}
                        className="flex items-start gap-2 text-sm text-gray-300"
                      >
                        <FaCheckCircle className="text-green-400 mt-0.5 flex-shrink-0" />
                        <span>{t(featureKey)}</span>
                      </motion.li>
                    ))}
                  </ul>

                  {/* زر الاختيار في الأسفل */}
                  <button
                    onClick={() => handleSelectTier(tier.id)}
                    className={`w-full py-2.5 rounded-xl font-bold transition-all mt-auto ${
                      isSelected
                        ? 'bg-white text-black hover:bg-gray-100'
                        : `bg-gradient-to-r ${tier.color} text-white hover:opacity-90 shadow-lg`
                    }`}
                  >
                    {isSelected ? t('sponsorship.selected') : t('sponsorship.selectTier')}
                  </button>
                </div>

                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -left-2 bg-green-500 rounded-full p-1 shadow-lg"
                  >
                    <FaCheckCircle className="text-white text-xl" />
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* ===== Modal for Sponsorship Form ===== */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-900/95 rounded-2xl w-full max-w-md p-6 border border-yellow-500/30 shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <FaPaperPlane className="text-yellow-400" />
                  {t('sponsorship.form.title')}
                </h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-white transition">
                  <FaTimes size={20} />
                </button>
              </div>

              {selectedTier && (
                <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-sm text-gray-300">
                    {t('sponsorship.selectedTier')}:{' '}
                    <span className="text-yellow-400 font-bold">
                      {t(tiers.find(t => t.id === selectedTier)?.titleKey)}
                    </span>
                  </p>
                </div>
              )}

              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-500/20 border border-green-500/50 text-green-300 p-4 rounded-xl mb-4"
                >
                  {t('sponsorship.form.success')}
                </motion.div>
              )}

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-500/20 border border-red-500/50 text-red-300 p-4 rounded-xl mb-4"
                >
                  {error}
                </motion.div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-2">
                    <FaUser /> {t('sponsorship.form.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={onInputChange}
                    required
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400 transition"
                    placeholder={t('sponsorship.form.namePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-2">
                    <FaEnvelope /> {t('sponsorship.form.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={onInputChange}
                    required
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400 transition"
                    placeholder={t('sponsorship.form.emailPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-2">
                    <FaBuilding /> {t('sponsorship.form.company')}
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={onInputChange}
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400 transition"
                    placeholder={t('sponsorship.form.companyPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-2">
                    <FaInfoCircle /> {t('sponsorship.form.message')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={onInputChange}
                    rows="4"
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400 transition resize-none"
                    placeholder={t('sponsorship.form.messagePlaceholder')}
                  />
                </div>

                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={loading || !selectedTier}
                  className={`w-full py-3 rounded-full font-bold transition-all flex items-center justify-center gap-2 ${
                    loading || !selectedTier
                      ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:scale-105 text-black shadow-lg'
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <FaPaperPlane /> {t('sponsorship.form.submit')}
                    </>
                  )}
                </button>

                <p className="text-center text-gray-500 text-xs mt-4">
                  {t('sponsorship.footer')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ========== Main Landing Page ==========
const LandingPage = ({ onAuthSuccess }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [continent, setContinent] = useState('Europe');
  const [error, setError] = useState('');
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [tempUserId, setTempUserId] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetStep, setResetStep] = useState('request');
  
  // ===== ✅ حالة رسالة إعادة الإرسال =====
  const [verificationMessage, setVerificationMessage] = useState('');

  // ========== State for Landing Stats ==========
  const [landingStats, setLandingStats] = useState({ totalUsers: 0, totalMatches: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  // ========== جلب الإحصائيات الديناميكية ==========
  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await fetchLandingStats();
        setLandingStats(res.data);
      } catch (err) {
        console.error('Failed to load landing stats:', err);
      } finally {
        setStatsLoading(false);
      }
    };
    loadStats();
  }, []);

  // ========== State for Sponsorship ==========
  const sponsorshipTiers = [
    {
      id: 'bronze',
      titleKey: 'sponsorship.tiers.bronze.title',
      price: '$29',
      periodKey: 'sponsorship.tiers.bronze.period',
      color: 'from-amber-600 to-orange-700',
      borderColor: 'border-amber-500/50',
      glowColor: 'shadow-amber-500/20',
      icon: <FaMedal className="text-amber-400" />,
      badgeKey: null,
      features: [
        'sponsorship.tiers.bronze.feature1',
        'sponsorship.tiers.bronze.feature2',
        'sponsorship.tiers.bronze.feature3',
        'sponsorship.tiers.bronze.feature4',
      ],
      spots: 5,
    },
    {
      id: 'silver',
      titleKey: 'sponsorship.tiers.silver.title',
      price: '$69',
      periodKey: 'sponsorship.tiers.silver.period',
      color: 'from-gray-300 to-gray-500',
      borderColor: 'border-gray-400/50',
      glowColor: 'shadow-gray-400/20',
      icon: <FaStar className="text-gray-300" />,
      badgeKey: 'sponsorship.badges.popular',
      features: [
        'sponsorship.tiers.silver.feature1',
        'sponsorship.tiers.silver.feature2',
        'sponsorship.tiers.silver.feature3',
        'sponsorship.tiers.silver.feature4',
        'sponsorship.tiers.silver.feature5',
      ],
      spots: 2,
    },
    {
      id: 'gold',
      titleKey: 'sponsorship.tiers.gold.title',
      price: '$149',
      periodKey: 'sponsorship.tiers.gold.period',
      color: 'from-yellow-400 to-yellow-600',
      borderColor: 'border-yellow-400/50',
      glowColor: 'shadow-yellow-400/30',
      icon: <FaCrown className="text-yellow-400" />,
      badgeKey: 'sponsorship.badges.exclusive',
      features: [
        'sponsorship.tiers.gold.feature1',
        'sponsorship.tiers.gold.feature2',
        'sponsorship.tiers.gold.feature3',
        'sponsorship.tiers.gold.feature4',
        'sponsorship.tiers.gold.feature5',
        'sponsorship.tiers.gold.feature6',
      ],
      spots: 1,
    },
    {
      id: 'diamond',
      titleKey: 'sponsorship.tiers.diamond.title',
      price: '$5,000',
      periodKey: 'sponsorship.tiers.diamond.period',
      color: 'from-blue-400 via-purple-500 to-pink-500',
      borderColor: 'border-purple-400/50',
      glowColor: 'shadow-purple-500/30',
      icon: <FaGem className="text-purple-400" />,
      badgeKey: 'sponsorship.badges.premium',
      features: [
        'sponsorship.tiers.diamond.feature1',
        'sponsorship.tiers.diamond.feature2',
        'sponsorship.tiers.diamond.feature3',
        'sponsorship.tiers.diamond.feature4',
        'sponsorship.tiers.diamond.feature5',
        'sponsorship.tiers.diamond.feature6',
        'sponsorship.tiers.diamond.feature7',
      ],
      spots: 1,
    },
  ];

  const [selectedTier, setSelectedTier] = useState(null);
  const [sponsorFormData, setSponsorFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [sponsorSuccess, setSponsorSuccess] = useState(false);
  const [sponsorError, setSponsorError] = useState(null);
  const [sponsorModalOpen, setSponsorModalOpen] = useState(false);

 // ========== State for Donation ==========
  const walletAddress = '0x29110EF001E0685F4Dc34dc7C7738A75f620d987';
  const [copySuccess, setCopySuccess] = useState(false);
  const [donationModalOpen, setDonationModalOpen] = useState(false);

  // ========== معالج طلب الرعاية ==========
  const handleSponsorSubmit = useCallback(async () => {
    if (!selectedTier) {
      setSponsorError(t('sponsorship.errors.selectTier'));
      return;
    }
    setSponsorLoading(true);
    setSponsorError(null);
    setSponsorSuccess(false);

    try {
      const selectedTierData = sponsorshipTiers.find(t => t.id === selectedTier);
      await API.post('/sponsorship/request', {
        ...sponsorFormData,
        tier: selectedTier,
        tierPrice: selectedTierData.price,
        tierName: t(selectedTierData.titleKey),
      });
      setSponsorSuccess(true);
      setSponsorFormData({ name: '', email: '', company: '', message: '' });
      setSelectedTier(null);
      setTimeout(() => {
        setSponsorSuccess(false);
        setSponsorModalOpen(false);
      }, 3000);
    } catch (err) {
      setSponsorError(err.response?.data?.error || t('common.somethingWrong'));
    } finally {
      setSponsorLoading(false);
    }
  }, [selectedTier, sponsorFormData, t]);

  const handleSponsorInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setSponsorFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // ========== ✅ دالة إعادة إرسال رمز التحقق ==========
  const handleResendVerification = useCallback(async () => {
    if (!tempUserId) {
      setVerificationMessage(t('auth.userIdMissing') || 'User ID missing');
      return;
    }
    setLoadingAuth(true);
    setVerificationMessage('');
    try {
      const res = await resendVerification(tempUserId);
      setVerificationMessage(res.data.message || t('auth.verificationCodeSentAgain') || '✅ Verification code resent successfully. Please check your email.');
      setTimeout(() => setVerificationMessage(''), 8000);
    } catch (err) {
      setVerificationMessage(err.response?.data?.error || t('auth.resendFailed') || '❌ Failed to resend code. Please try again.');
    } finally {
      setLoadingAuth(false);
    }
  }, [tempUserId, t]);

  // ========== معالج تقديم النموذج ==========
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');

    if (mode === 'forgotPassword') {
      if (!email) {
        setError(t('auth.emailRequired'));
        return;
      }
      setLoadingAuth(true);
      try {
        const res = await forgotPassword(email);
        setResetMessage(res.data.message || 'Reset code sent!');
        setResetStep('confirm');
        setMode('resetPassword');
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || t('common.somethingWrong'));
      } finally {
        setLoadingAuth(false);
      }
      return;
    }

    if (mode === 'resetPassword') {
      if (!resetToken || !password || !confirmPassword) {
        setError(t('auth.fillAllFields'));
        return;
      }
      if (password !== confirmPassword) {
        setError(t('auth.passwordsDoNotMatch'));
        return;
      }
      if (!isStrongPassword(password)) {
        setError(t('auth.weakPassword'));
        return;
      }
      setLoadingAuth(true);
      try {
        await resetPassword(resetToken, password);
        setResetMessage(t('auth.passwordResetSuccess'));
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        setResetToken('');
        setError('');
        setResetStep('request');
        setTimeout(() => setResetMessage(''), 5000);
      } catch (err) {
        setError(err.response?.data?.error || t('common.somethingWrong'));
      } finally {
        setLoadingAuth(false);
      }
      return;
    }

    if (mode === 'login') {
      if (!username || !password) {
        setError(t('auth.fillAllFields'));
        return;
      }
      setLoadingAuth(true);
      try {
        const res = await login(username, password);
        const userData = res.data.user;
        localStorage.setItem('userId', userData.id);
        localStorage.setItem('username', username);
        if (userData.continent) localStorage.setItem('continent', userData.continent);
        onAuthSuccess(userData.id);
      } catch (err) {
        // ✅ إذا كان الخطأ بسبب عدم التحقق من البريد
        if (err.response?.status === 403 && err.response?.data?.error?.includes('verify your email')) {
          // نفتح نافذة التحقق تلقائياً
          setTempUserId(err.response?.data?.userId || null);
          setShowVerification(true);
          setShowModal(true);
          setError('');
          setLoadingAuth(false);
          return;
        }
        setError(err.response?.data?.error || t('common.somethingWrong'));
      } finally {
        setLoadingAuth(false);
      }
      return;
    }

    if (mode === 'register') {
      if (password !== confirmPassword) {
        setError(t('auth.passwordsDoNotMatch'));
        return;
      }
      if (!email) {
        setError(t('auth.emailRequired'));
        return;
      }
      if (!isStrongPassword(password)) {
        setError(t('auth.weakPassword'));
        return;
      }
      setLoadingAuth(true);
      try {
        const res = await register(username, password, continent, email);
        setTempUserId(res.data.userId);
        setShowVerification(true);
        setError('');
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        setEmail('');
      } catch (err) {
        setError(err.response?.data?.error || t('common.somethingWrong'));
      } finally {
        setLoadingAuth(false);
      }
      return;
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setError(t('auth.enterVerificationCode'));
      return;
    }
    setLoadingAuth(true);
    try {
      await verifyEmail(tempUserId, verificationCode);
      const profileRes = await API.get(`/user/profile/${tempUserId}`);
      const userData = profileRes.data;
      localStorage.setItem('userId', tempUserId);
      localStorage.setItem('username', userData.username);
      if (userData.continent) localStorage.setItem('continent', userData.continent);
      onAuthSuccess(tempUserId);
    } catch (err) {
      setError(err.response?.data?.error || t('auth.verificationFailed'));
    } finally {
      setLoadingAuth(false);
    }
  };

  // ========== عرض محتوى مودال المصادقة ==========
  const renderModalContent = () => {
    if (showVerification) {
      return (
        <>
          <h2 className="text-2xl font-bold text-center mb-6">{t('auth.verifyEmail')}</h2>
          <p className="text-gray-300 text-sm text-center mb-4">
            {t('auth.verificationCodeSent')}
          </p>
          <form onSubmit={handleVerify}>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.verificationCode')}</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white text-center text-2xl tracking-widest focus:outline-none focus:border-yellow-400"
                placeholder="000000"
                maxLength="6"
                required
              />
            </div>
            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            {verificationMessage && <p className={`text-sm mb-3 ${verificationMessage.includes('✅') ? 'text-green-400' : 'text-yellow-400'}`}>{verificationMessage}</p>}
            <button
              type="submit"
              disabled={loadingAuth}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg transition"
            >
              {loadingAuth ? t('common.loading') : t('auth.verifyAndContinue')}
            </button>
          </form>
          <p className="text-center mt-4 text-sm text-gray-400">
            {t('auth.didntReceiveCode')}{' '}
            <button 
              onClick={handleResendVerification} 
              className="text-yellow-400 hover:underline"
              disabled={loadingAuth}
            >
              {loadingAuth ? t('common.loading') : t('auth.resend')}
            </button>
          </p>
        </>
      );
    }

    if (mode === 'forgotPassword') {
      return (
        <>
          <h2 className="text-2xl font-bold text-center mb-6">{t('auth.forgotPassword')}</h2>
          <p className="text-gray-300 text-sm text-center mb-4">
            {t('auth.forgotPasswordDesc')}
          </p>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400"
                required
              />
            </div>
            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            {resetMessage && <p className="text-green-400 text-sm mb-3">{resetMessage}</p>}
            <button
              type="submit"
              disabled={loadingAuth}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg transition"
            >
              {loadingAuth ? t('common.loading') : t('auth.sendResetCode')}
            </button>
          </form>
          <p className="text-center mt-4 text-sm text-gray-400">
            <button onClick={() => { setMode('login'); setError(''); setResetMessage(''); }} className="text-yellow-400 hover:underline">
              {t('auth.backToLogin')}
            </button>
          </p>
        </>
      );
    }

    if (mode === 'resetPassword') {
      return (
        <>
          <h2 className="text-2xl font-bold text-center mb-6">{t('auth.resetPassword')}</h2>
          <p className="text-gray-300 text-sm text-center mb-4">
            {t('auth.resetPasswordDesc')}
          </p>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.resetCode')}</label>
              <input
                type="text"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white text-center text-2xl tracking-widest focus:outline-none focus:border-yellow-400"
                placeholder="000000"
                maxLength="6"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.newPassword')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400"
                required
              />
              <p className="text-xs text-gray-400 mt-1">{t('auth.passwordRequirements')}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.confirmPassword')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400"
                required
              />
            </div>
            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            {resetMessage && <p className="text-green-400 text-sm mb-3">{resetMessage}</p>}
            <button
              type="submit"
              disabled={loadingAuth}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg transition"
            >
              {loadingAuth ? t('common.loading') : t('auth.resetPasswordButton')}
            </button>
          </form>
          <p className="text-center mt-4 text-sm text-gray-400">
            <button onClick={() => { setMode('login'); setError(''); setResetMessage(''); setResetStep('request'); }} className="text-yellow-400 hover:underline">
              {t('auth.backToLogin')}
            </button>
          </p>
        </>
      );
    }

    if (mode === 'login') {
      return (
        <>
          <h2 className="text-2xl font-bold text-center mb-6">{t('auth.welcomeBack')}</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.username')}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400"
                required
              />
            </div>
            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            <button
              type="submit"
              disabled={loadingAuth}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg transition"
            >
              {loadingAuth ? t('common.loading') : t('auth.login')}
            </button>
          </form>
          <div className="text-center mt-3">
            <button onClick={() => { setMode('forgotPassword'); setError(''); setResetMessage(''); setEmail(''); }} className="text-sm text-yellow-400 hover:underline">
              {t('auth.forgotPassword')}
            </button>
          </div>
          <p className="text-center mt-3 text-sm text-gray-400">
            {t('auth.noAccount')}{' '}
            <button onClick={() => { setMode('register'); setError(''); }} className="text-yellow-400 hover:underline">
              {t('auth.signUp')}
            </button>
          </p>
        </>
      );
    }

    if (mode === 'register') {
      return (
        <>
          <h2 className="text-2xl font-bold text-center mb-6">{t('auth.createAccount')}</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.username')}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-400"
                required
              />
              <p className="text-xs text-gray-400 mt-1">{t('auth.passwordRequirements')}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.confirmPassword')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-1">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-1 flex items-center gap-2"><FaGlobe /> {t('auth.continent')}</label>
              <select
                value={continent}
                onChange={(e) => setContinent(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                required
              >
                <option value="Europe">🇪🇺 {t('continents.europe')}</option>
                <option value="Asia">🇦🇸 {t('continents.asia')}</option>
                <option value="Africa">🇦🇫 {t('continents.africa')}</option>
                <option value="Americas">🌎 {t('continents.americas')}</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">{t('auth.continentDesc')}</p>
            </div>
            {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            <button
              type="submit"
              disabled={loadingAuth}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg transition"
            >
              {loadingAuth ? t('common.loading') : t('auth.signUp')}
            </button>
          </form>
          
          {/* ✅ زر إعادة فتح نافذة التحقق إذا أغلقت بالخطأ */}
          {tempUserId && !showVerification && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
              <p className="text-yellow-300 text-sm">
                {t('auth.verificationRequired') || '⚠️ You haven\'t verified your email yet.'}
              </p>
              <button 
                onClick={() => setShowVerification(true)}
                className="text-yellow-400 hover:underline font-bold text-sm mt-1"
              >
                {t('auth.openVerification') || 'Click here to open verification'}
              </button>
            </div>
          )}
          
          <p className="text-center mt-4 text-sm text-gray-400">
            {t('auth.haveAccount')}{' '}
            <button onClick={() => { setMode('login'); setError(''); }} className="text-yellow-400 hover:underline">
              {t('auth.login')}
            </button>
          </p>
        </>
      );
    }

    return null;
  };

  // ========== Demo players data ==========
  const demoPlayers = [
    { id: 1, name: 'Blazing Striker', position: 'Striker', rarity: 'gold', attack: 92, defense: 30, speed: 88, accuracy: 85, strength: 78, tactics: 70, special_ability: 'Super Scorer' },
    { id: 2, name: 'Silver Spear', position: 'Striker', rarity: 'silver', attack: 80, defense: 35, speed: 85, accuracy: 78, strength: 72, tactics: 65, special_ability: 'Speed Runner' },
    { id: 3, name: 'Bronze Hammer', position: 'Striker', rarity: 'bronze', attack: 70, defense: 40, speed: 75, accuracy: 70, strength: 68, tactics: 60, special_ability: 'Penalty Magnet' },
    { id: 4, name: 'Common Striker', position: 'Striker', rarity: 'common', attack: 55, defense: 45, speed: 60, accuracy: 55, strength: 58, tactics: 50, special_ability: null },
    { id: 5, name: 'Iron Shield', position: 'Defender', rarity: 'gold', attack: 40, defense: 94, speed: 70, accuracy: 60, strength: 88, tactics: 75, special_ability: 'Power Builder' },
    { id: 6, name: 'Silver Wall', position: 'Defender', rarity: 'silver', attack: 35, defense: 85, speed: 65, accuracy: 55, strength: 80, tactics: 70, special_ability: 'Clean Man' },
    { id: 7, name: 'Bronze Rock', position: 'Defender', rarity: 'bronze', attack: 30, defense: 75, speed: 58, accuracy: 50, strength: 72, tactics: 62, special_ability: 'Power Builder' },
    { id: 8, name: 'Common Defender', position: 'Defender', rarity: 'common', attack: 25, defense: 60, speed: 50, accuracy: 45, strength: 60, tactics: 52, special_ability: null },
    { id: 9, name: 'Maestro', position: 'Midfielder', rarity: 'gold', attack: 78, defense: 75, speed: 90, accuracy: 88, strength: 65, tactics: 92, special_ability: 'Playmaker' },
    { id: 10, name: 'Silver Engine', position: 'Midfielder', rarity: 'silver', attack: 70, defense: 68, speed: 82, accuracy: 80, strength: 60, tactics: 82, special_ability: 'Playmaker' },
    { id: 11, name: 'Bronze Dynamo', position: 'Midfielder', rarity: 'bronze', attack: 62, defense: 60, speed: 74, accuracy: 72, strength: 55, tactics: 72, special_ability: 'Speed Runner' },
    { id: 12, name: 'Common Midfielder', position: 'Midfielder', rarity: 'common', attack: 50, defense: 48, speed: 58, accuracy: 55, strength: 48, tactics: 58, special_ability: null },
    { id: 13, name: 'The Wall', position: 'Goalkeeper', rarity: 'gold', attack: 20, defense: 96, speed: 55, accuracy: 40, strength: 85, tactics: 88, special_ability: 'Super Saver' },
    { id: 14, name: 'Silver Guardian', position: 'Goalkeeper', rarity: 'silver', attack: 18, defense: 86, speed: 50, accuracy: 35, strength: 78, tactics: 80, special_ability: 'Super Saver' },
    { id: 15, name: 'Bronze Keeper', position: 'Goalkeeper', rarity: 'bronze', attack: 15, defense: 76, speed: 45, accuracy: 30, strength: 70, tactics: 72, special_ability: null },
    { id: 16, name: 'Common Keeper', position: 'Goalkeeper', rarity: 'common', attack: 12, defense: 62, speed: 40, accuracy: 28, strength: 60, tactics: 62, special_ability: null },
  ];

  // ===== إضافة State للمودال =====
  const [showTrailer, setShowTrailer] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-green-900 to-emerald-950 text-white overflow-x-hidden" dir={i18n.dir()}>
      {/* Animated Grass Pattern */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,255,0,0.08)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      {/* Floating Footballs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, -50, 0] }}
        transition={{ repeat: Infinity, duration: 15, ease: "linear" }}
        className="fixed top-20 left-10 opacity-20 pointer-events-none"
      ><FaFutbol className="text-white text-7xl" /></motion.div>
      <motion.div
        animate={{ x: [0, -80, 0], y: [0, 60, 0] }}
        transition={{ repeat: Infinity, duration: 18, ease: "linear", delay: 2 }}
        className="fixed bottom-40 right-10 opacity-20 pointer-events-none"
      ><FaFutbol className="text-white text-6xl" /></motion.div>

      {/* Navbar */}
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-md border-b border-yellow-500/30 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/img/logo.png" alt="Soccer Up" className="h-16 sm:h-14 md:h-16 w-auto" onError={(e) => e.target.src = '/img/player.png'} />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-yellow-400 to-emerald-400 bg-clip-text text-transparent">Soccer Up</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:block">
              <LanguageSelector />
            </div>
            <div className="hidden md:flex gap-3">
              <button onClick={() => { setMode('login'); setShowModal(true); setShowVerification(false); setError(''); }} className="bg-transparent border border-yellow-400 hover:bg-yellow-400/20 text-yellow-400 font-bold py-2 px-5 rounded-full transition">{t('auth.login')}</button>
              <button onClick={() => { setMode('register'); setShowModal(true); setShowVerification(false); setError(''); }} className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-5 rounded-full transition transform hover:scale-105">{t('auth.signUp')}</button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-yellow-400 text-2xl">
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden mt-3 pt-2 border-t border-yellow-500/30 flex flex-col gap-2">
            <div className="mb-2">
              <LanguageSelector />
            </div>
            <button onClick={() => { setMode('login'); setShowModal(true); setShowVerification(false); setMobileMenuOpen(false); }} className="w-full bg-transparent border border-yellow-400 hover:bg-yellow-400/20 text-yellow-400 font-bold py-2 rounded-full">{t('auth.login')}</button>
            <button onClick={() => { setMode('register'); setShowModal(true); setShowVerification(false); setMobileMenuOpen(false); }} className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-full">{t('auth.signUp')}</button>
          </div>
        )}
      </motion.nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <h1 className="text-5xl md:text-7xl font-extrabold mb-4">{t('landing.heroTitle')} <span className="bg-gradient-to-r from-yellow-400 to-emerald-400 bg-clip-text text-transparent">{t('landing.heroHighlight')}</span></h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-8">
            {t('landing.heroDesc')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={() => { setMode('register'); setShowModal(true); setShowVerification(false); }} className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold py-3 px-8 rounded-full text-lg shadow-lg hover:scale-105 transition flex items-center gap-2">{t('landing.playNow')} <FaArrowRight /></button>
            {/* ✅ زر Watch Trailer يفتح المودال */}
            <button
              onClick={() => setShowTrailer(true)}
              className="bg-transparent border border-white hover:bg-white/10 px-8 py-3 rounded-full text-white transition-all duration-300 hover:scale-105"
            >
              {t('landing.watchTrailer')}
            </button>
          </div>
        </motion.div>

        {/* Cards Showcase Section */}
        <div className="mt-20">
          <motion.h3 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-yellow-300 to-yellow-500 bg-clip-text text-transparent"
          >
            {t('landing.sampleCards')}
          </motion.h3>
          <div className="flex justify-center flex-wrap gap-8">
            {demoPlayers.map((player) => (
              <FlipCard key={player.id} player={player} />
            ))}
          </div>
          <p className="text-sm text-yellow-300 mt-6 animate-pulse text-center">
            {t('landing.tapToFlip')}
          </p>
        </div>
      </section>

      {/* Stats Bar - ✅ ديناميكي */}
      <div className="bg-black/40 backdrop-blur-sm py-6 border-y border-yellow-500/20">
        <div className="container mx-auto px-6 flex flex-wrap justify-center gap-12 text-center">
          <div>
            <div className="text-3xl font-bold text-yellow-400">
              {statsLoading ? '...' : <Counter end={landingStats.totalUsers} />}+
            </div>
            <div className="text-sm">{t('landing.activeManagers')}</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-yellow-400">
              {statsLoading ? '...' : <Counter end={landingStats.totalMatches} />}+
            </div>
            <div className="text-sm">{t('landing.dailyMatches')}</div>
          </div>
          <div><div className="text-3xl font-bold text-yellow-400">6</div><div className="text-sm">{t('landing.formations')}</div></div>
          <div><div className="text-3xl font-bold text-yellow-400">7</div><div className="text-sm">{t('landing.specialAbilities')}</div></div>
        </div>
      </div>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-4xl font-bold text-center mb-12">{t('landing.whyTitle')} <span className="text-yellow-400">Soccer Up</span>?</motion.h2>
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard icon={<FaGem />} title={t('landing.feature1.title')} description={t('landing.feature1.desc')} delay={0} />
            <FeatureCard icon={<FaExchangeAlt />} title={t('landing.feature2.title')} description={t('landing.feature2.desc')} delay={0.1} />
            <FeatureCard icon={<FaUsers />} title={t('landing.feature3.title')} description={t('landing.feature3.desc')} delay={0.2} />
            <FeatureCard icon={<FaChartLine />} title={t('landing.feature4.title')} description={t('landing.feature4.desc')} delay={0.3} />
            <FeatureCard icon={<FaCrown />} title={t('landing.feature5.title')} description={t('landing.feature5.desc')} delay={0.4} />
            <FeatureCard icon={<FaTrophy />} title={t('landing.feature6.title')} description={t('landing.feature6.desc')} delay={0.5} />
          </div>
        </div>
      </section>

      {/* How to Play */}
      <section className="py-20 px-6 bg-black/30">
        <div className="container mx-auto">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-4xl font-bold text-center mb-12">{t('landing.howToPlayTitle')}</motion.h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: t('landing.step1.title'), icon: <FaGem />, desc: t('landing.step1.desc') },
              { step: '02', title: t('landing.step2.title'), icon: <FaExchangeAlt />, desc: t('landing.step2.desc') },
              { step: '03', title: t('landing.step3.title'), icon: <FaUsers />, desc: t('landing.step3.desc') },
              { step: '04', title: t('landing.step4.title'), icon: <FaTrophy />, desc: t('landing.step4.desc') },
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} viewport={{ once: true }} className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center border border-white/20 hover:border-yellow-400/40 transition group">
                <div className="text-5xl font-black text-yellow-400/30 mb-2">{item.step}</div>
                <div className="text-yellow-400 text-3xl mb-2">{item.icon}</div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-300">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

{/* ========== Special Abilities Showcase ========== */}
<section className="py-20 px-6">
  <div className="container mx-auto">
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-4xl font-bold text-center mb-4"
    >
      {t('landing.specialAbilitiesTitle')}
    </motion.h2>
    <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
      {t('landing.specialAbilitiesDesc')}
    </p>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
      {[
        { 
          name: 'Playmaker', 
          icon: <GiSoccerBall className="text-5xl text-purple-300" />, 
          effect: t('abilities.playmakerEffect'), 
          color: 'from-purple-500 to-indigo-600' 
        },
        { 
          name: 'Super Scorer', 
          icon: <FaBullseye className="text-5xl text-red-300" />, 
          effect: t('abilities.superScorerEffect'), 
          color: 'from-red-500 to-orange-600' 
        },
        { 
          name: 'Super Saver', 
          icon: <GiShield className="text-5xl text-blue-300" />, 
          effect: t('abilities.superSaverEffect'), 
          color: 'from-blue-500 to-cyan-600' 
        },
        { 
          name: 'Speed Runner', 
          icon: <GiSpeedometer className="text-5xl text-green-300" />, 
          effect: t('abilities.speedRunnerEffect'), 
          color: 'from-green-500 to-emerald-600' 
        },
        { 
          name: 'Power Builder', 
          icon: <FaShieldAlt className="text-5xl text-yellow-300" />, 
          effect: t('abilities.powerBuilderEffect'), 
          color: 'from-yellow-500 to-amber-600' 
        },
        { 
          name: 'Clean Man', 
          icon: <FaRegHandshake className="text-5xl text-teal-300" />, 
          effect: t('abilities.cleanManEffect'), 
          color: 'from-teal-500 to-cyan-600' 
        },
        { 
          name: 'Penalty Magnet', 
          icon: <FaMagnet className="text-5xl text-pink-300" />, 
          effect: t('abilities.penaltyMagnetEffect'), 
          color: 'from-pink-500 to-rose-600' 
        },
      ].map((ability) => (
        <motion.div
          key={ability.name}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          whileHover={{ y: -5, scale: 1.05 }}
          className={`bg-gradient-to-br ${ability.color} rounded-2xl p-6 text-center shadow-lg border border-white/10 hover:shadow-2xl transition-all`}
        >
          {/* ✅ التعديل هنا: إضافة flex justify-center لتوسيط الأيقونة */}
          <div className="flex justify-center mb-3">{ability.icon}</div>
          <h4 className="text-white font-bold text-lg">{ability.name}</h4>
          <p className="text-white/80 text-sm mt-1">{ability.effect}</p>
        </motion.div>
      ))}
    </div>
  </div>
</section>

{/* ========== Formations Showcase ========== */}
<section className="py-20 px-6 bg-black/30">
  <div className="container mx-auto">
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-4xl font-bold text-center mb-12"
    >
      {t('landing.formationsTitle')}
    </motion.h2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[
        { 
          id: 'aggressive', 
          name: t('formation.aggressive'), 
          short: '2-1-2', 
          icon: <FaFire className="text-5xl text-red-400" />, 
          desc: t('formation.aggressiveDesc'), 
          color: 'from-red-600 to-red-800' 
        },
        { 
          id: 'counter', 
          name: t('formation.counter'), 
          short: '2-2-1', 
          icon: <FaBolt className="text-5xl text-yellow-400" />, 
          desc: t('formation.counterDesc'), 
          color: 'from-yellow-600 to-yellow-800' 
        },
        { 
          id: 'traditionalAttack', 
          name: t('formation.traditionalAttack'), 
          short: '2-2-1', 
          icon: <FaFutbol className="text-5xl text-green-400" />, 
          desc: t('formation.traditionalAttackDesc'), 
          color: 'from-green-600 to-green-800' 
        },
        { 
          id: 'traditionalDefense', 
          name: t('formation.traditionalDefense'), 
          short: '3-1-1', 
          icon: <FaShieldAlt className="text-5xl text-blue-400" />, 
          desc: t('formation.traditionalDefenseDesc'), 
          color: 'from-blue-600 to-blue-800' 
        },
        { 
          id: 'highPress', 
          name: t('formation.highPress'), 
          short: '4-3-3', 
          icon: <FaRunning className="text-5xl text-orange-400" />, 
          desc: t('formation.highPressDesc'), 
          color: 'from-orange-600 to-orange-800' 
        },
        { 
          id: 'advancedDefense', 
          name: t('formation.advancedDefense'), 
          short: '5-3-2', 
          icon: <FaCompress className="text-5xl text-cyan-400" />, 
          desc: t('formation.advancedDefenseDesc'), 
          color: 'from-cyan-600 to-cyan-800' 
        },
      ].map((formation) => (
        <motion.div
          key={formation.id}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          whileHover={{ y: -5 }}
          className={`bg-gradient-to-br ${formation.color} rounded-2xl p-6 border border-white/10 shadow-lg hover:shadow-2xl transition-all`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-5xl">{formation.icon}</div>
            <span className="bg-black/40 px-3 py-1 rounded-full text-sm font-mono text-white">{formation.short}</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-1">{formation.name}</h3>
          <p className="text-white/80 text-sm">{formation.desc}</p>
        </motion.div>
      ))}
    </div>
  </div>
</section>

      {/* ========== Leagues Showcase (مكتمل) ========== */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center mb-4"
          >
            {t('landing.leagueSystemTitle')}
          </motion.h2>
          <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
            {t('landing.leagueSystemDesc')}
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { key: 'domestic', title: t('competition.domestic'), icon: <FaGlobe className="text-green-400" />, desc: t('landing.domesticDesc'), color: 'from-green-900/40 to-green-800/20', border: 'border-green-500/30' },
              { key: 'champions', title: t('competition.champions'), icon: <FaTrophy className="text-yellow-400" />, desc: t('landing.championsDesc'), color: 'from-yellow-900/40 to-yellow-800/20', border: 'border-yellow-500/30' },
              { key: 'europa', title: t('competition.europa'), icon: <FaMedal className="text-blue-400" />, desc: t('landing.europaDesc'), color: 'from-blue-900/40 to-blue-800/20', border: 'border-blue-500/30' },
            ].map((league) => (
              <motion.div
                key={league.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                whileHover={{ y: -5 }}
                className={`bg-gradient-to-br ${league.color} rounded-2xl p-6 border ${league.border} shadow-lg hover:shadow-2xl transition-all`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-4xl">{league.icon}</div>
                  <h3 className="text-2xl font-bold text-white">{league.title}</h3>
                </div>
                <p className="text-gray-300 text-sm">{league.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SPONSORSHIP SECTION ========== */}
      <SponsorshipSection
        tiers={sponsorshipTiers}
        selectedTier={selectedTier}
        setSelectedTier={setSelectedTier}
        formData={sponsorFormData}
        onInputChange={handleSponsorInputChange}
        onSubmit={handleSponsorSubmit}
        loading={sponsorLoading}
        success={sponsorSuccess}
        error={sponsorError}
        t={t}
        isModalOpen={sponsorModalOpen}
        openModal={() => setSponsorModalOpen(true)}
        closeModal={() => {
          setSponsorModalOpen(false);
          setSponsorError(null);
          setSponsorSuccess(false);
        }}
      />

      {/* ========== DONATION SECTION ========== */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <button
              onClick={() => setDonationModalOpen(true)}
              className="group relative px-8 py-4 bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border border-yellow-500/40 rounded-full hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-yellow-500/20 flex items-center gap-3"
            >
              <span className="text-yellow-400 text-2xl group-hover:rotate-12 transition-transform duration-300">☕</span>
              <span className="text-white font-bold text-lg">{t('buyMeCoffee.title')}</span>
              <span className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400/0 via-yellow-400/5 to-yellow-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* ===== Modal for Donation ===== */}
      <AnimatePresence>
        {donationModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDonationModalOpen(false)}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-900/95 rounded-2xl w-full max-w-md p-6 border border-yellow-500/30 shadow-2xl"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <FaCoffee className="text-yellow-400 text-3xl" />
                  {t('buyMeCoffee.title')}
                </h2>
                <button onClick={() => setDonationModalOpen(false)} className="text-gray-400 hover:text-white transition">
                  <FaTimes size={20} />
                </button>
              </div>

              <p className="text-gray-300 text-center mb-6 text-sm">
                {t('buyMeCoffee.description')}
              </p>

              <div className="flex flex-col items-center">
                {/* QR Code */}
                <div className="bg-white p-4 rounded-xl shadow-lg">
                  <QRCodeCanvas
                    value={walletAddress}
                    size={180}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="H"
                    includeMargin={false}
                  />
                </div>

                {/* Address and Copy Button */}
                <div className="mt-4 w-full">
                  <div className="flex items-center gap-2 bg-gray-800/80 p-3 rounded-lg border border-gray-700">
                    <code className="text-xs text-yellow-300 break-all flex-1 font-mono select-all">
                      {walletAddress}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(walletAddress);
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      }}
                      className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm hover:bg-yellow-500/30 transition flex-shrink-0"
                    >
                      {copySuccess ? t('common.copied') : t('common.copy')}
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-center gap-2 text-xs text-yellow-500 bg-yellow-500/10 p-2 rounded-lg border border-yellow-500/20">
                    <FaInfoCircle className="text-yellow-400 text-sm" />
                    <span>{t('buyMeCoffee.supportedNetworks')}</span>
                  </div>
                  <p className="text-center text-gray-500 text-xs mt-4">
                    {t('buyMeCoffee.footer')}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-24 px-6 text-center bg-gradient-to-r from-yellow-600/20 to-emerald-600/20">
        <h2 className="text-4xl font-bold mb-4">{t('landing.ctaTitle')} <span className="text-yellow-400">{t('landing.ctaHighlight')}</span>?</h2>
        <p className="text-xl text-gray-300 mb-8">{t('landing.ctaDesc')}</p>
        <button onClick={() => { setMode('register'); setShowModal(true); setShowVerification(false); }} className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 px-12 rounded-full text-2xl transition transform hover:scale-105 shadow-2xl flex items-center gap-3 mx-auto">
          <FaFire /> {t('landing.signUpNow')}
        </button>
      </section>

{/* Footer */}
<footer className="bg-black/40 backdrop-blur-sm py-8 text-center text-gray-400 text-sm border-t border-white/10">
  <div className="container mx-auto px-4">
    <p className="mb-2">© 2026 Soccer Up – {t('landing.footer')}</p>
    <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
      <a href="https://x.com/soccerup26" className="hover:text-yellow-400 transition-colors duration-300">Twitter</a>
      <a href="https://discord.com/users/soccerup" className="hover:text-yellow-400 transition-colors duration-300">Discord</a>
      <a href="/terms.html" className="hover:text-yellow-400 transition-colors duration-300">
        {t('footer.terms')}
      </a>
      <a href="/privacy.html" className="hover:text-yellow-400 transition-colors duration-300">
        {t('footer.privacy')}
      </a>
    </div>
  </div>
</footer>

      {/* Auth Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-gray-800 rounded-2xl w-full max-w-md p-6 relative border border-yellow-500/30">
            {/* ✅ زر الإغلاق يغلق المودال فقط ولا يمسح tempUserId */}
            <button 
              onClick={() => { 
                setShowModal(false); 
                // لا نمسح showVerification أو tempUserId
              }} 
              className="absolute top-3 right-3 text-gray-400 hover:text-white"
            >
              <FaTimes size={24} />
            </button>
            {renderModalContent()}
          </motion.div>
        </div>
      )}

      {/* ===== Trailer Modal – باستخدام عنصر <video> محلي ===== */}
      <AnimatePresence>
        {showTrailer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTrailer(false)}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl bg-black/90 rounded-2xl overflow-hidden shadow-2xl border border-yellow-500/30"
            >
              <button
                onClick={() => setShowTrailer(false)}
                className="absolute top-3 right-3 z-10 text-white/80 hover:text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-all duration-300"
              >
                <FaTimes size={24} />
              </button>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <video
                  className="absolute top-0 left-0 w-full h-full object-contain bg-black"
                  src="/videos/trailer.mp4"
                  controls
                  autoPlay
                  playsInline
                  poster="/videos/trailer-poster.jpg"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white text-sm md:text-base font-medium text-center">
                  🎬 {t('landing.watchTrailer')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default LandingPage;